import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { useUserData } from '../hooks/useUserData';
import { useWorkspace } from '../contexts/WorkspaceContext';
import SimpleWorkspaceDropdown from './SimpleWorkspaceDropdown';

import CreateOptionsModal from './CreateOptionsModal';
import DocumentsList from './DocumentsList';
import InvoiceModal from './InvoiceModal';
import AgreementModal from './AgreementModal';
// import MfaBanner from './MfaBanner'; // DISABLED: 2FA not working
import { getDoc, doc } from 'firebase/firestore';
import { BalanceOverview } from './fraud';
import { PayoutService } from '../services/PayoutService';

const ROUTES = {
  HOME: { path: '/app', label: 'HOME' },
  LEADS: { path: '/app/leads', label: 'LEADS' },
  BOOKING: { path: '/app/booking', label: 'BOOKING' },
  TEAM: { path: '/app/team', label: 'TEAM' },
};

function NavigationBar({ setShowComposeModal, isMenuOpen, setIsMenuOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOrganizationWorkspace } = useWorkspace();

  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [showDocuments, setShowDocuments] = useState({
    invoices: false,
    agreements: false,
  });

  const [balance, setBalance] = useState({
    available: 0,
    pending: 0
  });
  const [isLoadingBalance, setIsLoadingBalance] = useState(true);
  const [balanceError, setBalanceError] = useState('');
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [transactionsError, setTransactionsError] = useState('');

  const [balanceOverviewData, setBalanceOverviewData] = useState(null);
  const [isLoadingBalanceOverview, setIsLoadingBalanceOverview] = useState(false);

  const { stats = {}, documents = {}, userData } = useUserData();

  /* ───────── Fetch user data and Stripe balance ───────── */
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setIsLoadingBalance(false);
      return;
    }

    const fetchUserData = async (userId) => {
      setIsLoadingBalance(true);
      setBalanceError('');
      
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.stripeAccountId) {
            fetchBalance(userData.stripeAccountId);
          } else {
            setBalanceError('Stripe account not connected');
            setIsLoadingBalance(false);
          }
        } else {
          throw new Error('User document does not exist.');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setBalanceError('Failed to fetch user data');
        setIsLoadingBalance(false);
      }
    };

    const fetchBalance = async (accountId) => {
      try {
        const response = await fetch(`https://klorah-fast-server-my8l.onrender.com/api/balance/${accountId}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch balance: ${response.statusText}`);
        }
        const data = await response.json();
        setBalance({
          available: Number(data.available[0]?.amount || 0) / 100,
          pending: Number(data.pending[0]?.amount || 0) / 100,
        });
      } catch (error) {
        console.error('Fetch error:', error);
        setBalanceError('Failed to fetch balance');
      } finally {
        setIsLoadingBalance(false);
      }
    };

    fetchUserData(user.uid);
  }, []);

  /* ───────── Fetch enhanced balance overview ───────── */
  useEffect(() => {
    if (!showBalanceModal) return;
    const user = auth.currentUser;
    if (!user || !userData?.stripeAccountId) return;

    const fetchBalanceOverview = async () => {
      setIsLoadingBalanceOverview(true);
      try {
        const payoutSvc = new PayoutService();
        const data = await payoutSvc.getBalanceOverview(user.uid, userData.stripeAccountId);
        setBalanceOverviewData(data);
      } catch (error) {
        console.error('Error fetching balance overview:', error);
      } finally {
        setIsLoadingBalanceOverview(false);
      }
    };

    fetchBalanceOverview();
  }, [showBalanceModal, userData]);

  /* ───────── Fetch Stripe transactions ───────── */
  useEffect(() => {
    if (!showBalanceModal || !userData?.stripeAccountId) return;

    const fetchTransactions = async () => {
      setIsLoadingTransactions(true);
      setTransactionsError('');

      try {
        const res = await fetch(
          `https://klorah-fast-server-my8l.onrender.com/api/transactions/${userData.stripeAccountId}`,
        );
        if (!res.ok) throw new Error(res.statusText);
        const json = await res.json();
        
        // Log the response to see its structure
        console.log('Transactions API response:', json);
        
        // Handle both potential response structures
        const transactionsData = json.transactions?.data || json.transactions || [];
        console.log('Extracted transactions:', transactionsData);
        
        setTransactions(transactionsData);
      } catch (err) {
        console.error('Transaction fetch error:', err);
        setTransactionsError('Failed to fetch transactions');
      } finally {
        setIsLoadingTransactions(false);
      }
    };

    fetchTransactions();
  }, [showBalanceModal, userData]);

  /* ───────── Helpers ───────── */
  const handleDocumentClick = (type) => {
    if (type === 'CLIENTS') {
      navigate('/app/clients');
      return;
    }
    // Map 'CONTRACTS' to 'agreements' for internal state
    const key = type === 'CONTRACTS' ? 'agreements' : type.toLowerCase();
    setShowDocuments((prev) => ({ ...prev, [key]: true }));
  };

  const closeDoc = (key) =>
    setShowDocuments((prev) => ({ ...prev, [key]: false }));

  const isActive = (path) => {
    // Special case for HOME - only match exact /app path
    if (path === '/app') {
      return location.pathname === '/app';
    }
    return location.pathname.startsWith(path);
  };

  /* ───────── Format date ───────── */
  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  /* ───────── Sidebar content ───────── */
  const NavContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 flex-1">
        {/* Logo */}
        <div className="flex items-center gap-2.5 mb-8">
          <h1 className="text-lg font-light tracking-[0.4em] text-gray-900 dark:text-white">
            KLORAH
          </h1>
          <span className="px-1.5 py-0.5 text-[9px] bg-gray-100 dark:bg-[#1A1A1A] rounded text-gray-400 font-medium">
            PRO
          </span>
        </div>

        {/* Workspace Dropdown */}
        <div className="mb-6">
          <SimpleWorkspaceDropdown />
        </div>

        {/* Documents */}
        <div className="space-y-6">
          <div className="space-y-0.5">
            {['Invoices', 'Contracts', 'Clients'].map((t) => {
              // Map display names to internal stat keys
              const statKey = t === 'Contracts' ? 'agreementsCount' : `${t.toLowerCase()}Count`;
              return (
                <div
                  key={t}
                  onClick={() => handleDocumentClick(t.toUpperCase())}
                  className="flex items-center justify-between py-2 px-2.5 rounded-lg cursor-pointer hover:bg-brand/5 dark:hover:bg-[#1A1A1A] transition-colors group"
                >
                  <span className="text-[13px] text-gray-600 dark:text-[#999999] group-hover:text-brand dark:group-hover:text-white transition-colors">
                    {t}
                  </span>
                  <span className="text-[11px] text-gray-400 dark:text-[#666666]">
                    {stats[statKey] ?? 0}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Routes */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-[#555555] mb-2 px-2.5">
              Pages
            </p>
            <div className="space-y-0.5">
              {Object.values(ROUTES)
                .filter(({ path }) => {
                  if (path === '/app/team') return isOrganizationWorkspace;
                  return true;
                })
                .map(({ path, label }) => (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    className={`flex items-center w-full py-2 px-2.5 rounded-lg text-[13px] transition-colors ${
                      isActive(path)
                        ? 'bg-brand text-white'
                        : 'text-gray-600 dark:text-[#999999] hover:bg-brand/5 dark:hover:bg-[#1A1A1A] hover:text-brand dark:hover:text-white'
                    }`}
                  >
                    {label.charAt(0) + label.slice(1).toLowerCase()}
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  );

  /* ───────── Return ───────── */
  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:block lg:w-56 bg-[#FAFAFA] dark:bg-[#0A0A0A] h-screen">
        <NavContent />
      </div>

      {/* Mobile overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-50 flex lg:hidden"
          onClick={() => setIsMenuOpen(false)}
        >
          <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" />
          <nav
            className={`relative w-64 bg-white dark:bg-black border-r border-gray-200/40 dark:border-[#151515] h-full shadow-xl transition-transform duration-300 ${
              isMenuOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <NavContent />
          </nav>
        </div>
      )}

      {/* Balance Modal */}
      {showBalanceModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setShowBalanceModal(false)}
        >
          <div
            className="bg-white dark:bg-[#111111] w-96 max-h-[85vh] rounded-2xl overflow-hidden border border-gray-200 dark:border-[#1A1A1A] shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 border-b border-gray-100 dark:border-[#1A1A1A]">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                Balance & Payouts
              </h2>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto max-h-[60vh]">
              {/* Enhanced Balance Overview with fraud prevention data */}
              <BalanceOverview
                balanceData={balanceOverviewData}
                isLoading={isLoadingBalanceOverview}
              />

              {/* Legacy Pending Balance fallback */}
              {!balanceOverviewData && !isLoadingBalanceOverview && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-[#555555] font-medium">
                    Pending Balance
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
                    ${balance.pending.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-[#999999] leading-relaxed">
                    Funds are transferring to your bank. This typically takes 2-3 business days.
                  </p>
                </div>
              )}
            
              {/* Transactions section */}
              <div className="mt-5 pt-4 border-t border-gray-100 dark:border-[#1A1A1A]">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-[#555555] font-medium mb-3">
                  Recent Transactions
                </p>

                {isLoadingTransactions ? (
                  <div className="space-y-2 animate-pulse">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-12 bg-gray-100 dark:bg-[#1A1A1A] rounded-lg" />
                    ))}
                  </div>
                ) : transactionsError ? (
                  <p className="text-xs text-red-500">{transactionsError}</p>
                ) : transactions.length === 0 ? (
                  <p className="text-xs text-gray-400 dark:text-[#666666]">No transactions yet</p>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto show-scrollbar">
                    {transactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="flex justify-between items-center px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
                            {transaction.description || 'Payment'}
                          </p>
                          <p className="text-[10px] text-gray-400 dark:text-[#666666]">
                            {formatDate(transaction.created)}
                          </p>
                        </div>
                        <p className={`text-[13px] font-semibold tabular-nums flex-shrink-0 ml-3 ${
                          transaction.status === 'succeeded'
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-gray-500 dark:text-[#999999]'
                        }`}>
                          ${(transaction.amount / 100).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 dark:border-[#1A1A1A] space-y-2">
              <button
                onClick={() =>
                  window.open(
                    'https://connect.stripe.com/app/express#balances',
                    '_blank',
                  )
                }
                className="w-full py-2.5 bg-brand hover:bg-brand-hover rounded-xl text-sm font-medium text-white transition-colors"
              >
                View in Stripe
              </button>
              <button
                onClick={() => setShowBalanceModal(false)}
                className="w-full py-2.5 rounded-xl text-sm font-medium text-gray-500 dark:text-[#999999] hover:text-gray-700 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateOptionsModal
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
        onCreateEmail={() => {
          setShowCreateOptions(false);
          setShowComposeModal(true);
        }}
        onCreateInvoice={() => {
          setShowCreateOptions(false);
          setShowInvoiceModal(true);
        }}
        onCreateAgreement={() => {
          setShowCreateOptions(false);
          setShowAgreementModal(true);
        }}
      />

      {showDocuments.invoices && (
        <DocumentsList
          documents={documents.invoices}
          surveys={documents.surveys}
          type="invoices"
          onClose={() => closeDoc('invoices')}
          onCreateNew={() => {
            closeDoc('invoices');
            setShowInvoiceModal(true);
          }}
        />
      )}

      {showDocuments.agreements && (
        <DocumentsList
          documents={documents.agreements}
          type="agreements"
          onClose={() => closeDoc('agreements')}
          onCreateNew={() => {
            closeDoc('agreements');
            setShowAgreementModal(true);
          }}
        />
      )}

      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          closeDoc('invoices');
        }}
        user={auth.currentUser}
      />

      <AgreementModal
        isOpen={showAgreementModal}
        onClose={() => {
          setShowAgreementModal(false);
          closeDoc('agreements');
        }}
      />
    </>
  );
}

export default NavigationBar;